import {
  useContext,
  useRef,
  type ButtonHTMLAttributes,
  type MouseEvent,
  type PropsWithChildren,
  type ReactNode,
} from 'react';
import { useHoverDirty } from 'react-use';
import styled from 'styled-components';
import { modeColor } from '../../styles';
import { Button } from '../Button';
import { Box, HStack, VStack, type BoxProps } from '../Core';
import { FormControlSizes } from '../Form/types';
import { IconName } from '../Icons';
import { usePowerSearchContext } from './PowerSearchContext';
import { PowerSearchHighlightContext } from './PowerSearchHighlightContext';

/** Describes the affordance shown at the end of each result row. */
export type PowerSearchItemAction = {
  /** Badge shown when the row is highlighted or hovered. */
  label: ReactNode;
  /**
   * Accessible name of the result row. When omitted and `label` is a string, that string is used.
   * An `aria-label` passed on the row overrides this.
   */
  ariaLabel?: string;
};

type PowerSearchItemProps = PropsWithChildren<
  Omit<BoxProps, 'children' | 'id' | 'onClick'> &
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children' | 'id' | 'onClick' | 'type'> & {
      id: string;
      onSelect?(id: string): void;
      /** Affordance badge shown at the row's trailing edge — required so every result communicates its intent. */
      action: PowerSearchItemAction;
    }
>;

export function PowerSearchItem({ children, id, onSelect, action, ...props }: PowerSearchItemProps) {
  const highlightedID = useContext(PowerSearchHighlightContext);
  const isHighlighted = id === highlightedID;
  const {
    modal: { close },
  } = usePowerSearchContext();
  const ref = useRef<HTMLDivElement>(null);
  const isHovering = useHoverDirty(ref);
  const ariaLabel =
    props['aria-label'] ?? action.ariaLabel ?? (typeof action.label === 'string' ? action.label : undefined);

  const handleClick = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    close();
    onSelect?.(id);
  };

  return (
    <Wrapper
      ref={ref}
      id={id}
      data-testid="power-search-item"
      data-highlighted={isHighlighted}
      className="power-search-item"
      onClick={handleClick}
      $isHighlighted={isHighlighted}
      {...props}
      aria-label={ariaLabel}
    >
      <HStack justifyContent="space-between" gap="spacing8">
        <VStack flex="1" overflow="hidden" alignItems="flex-start">
          {children}
        </VStack>
        {(isHighlighted || isHovering) && (
          <Button
            forwardedAs="span"
            aria-hidden
            size={FormControlSizes.Small}
            endIcon={IconName.ChevronRight}
            data-testid="power-search-action-tag"
          >
            {action.label}
          </Button>
        )}
      </HStack>
    </Wrapper>
  );
}

type WrapperProps = Omit<BoxProps, 'onClick'> &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onClick'> & {
    $isHighlighted: boolean;
  };

const Wrapper = styled(Box).attrs<WrapperProps>({
  as: 'button',
  type: 'button',
})<WrapperProps>`
  background: var(--colors-gray-020);
  appearance: none;
  border: 0;
  display: block;
  text-align: initial;
  min-height: 42px;
  color: var(--colorTextDefault);
  outline: none;
  border-top: 1px solid ${({ theme }) => modeColor(theme, theme.colors.black.mute, theme.colors.black.dim)};
  min-height: 42px;

  ${({ $isHighlighted }) => $isHighlighted && `background: var(--colors-gray-030)`};
  &:hover {
    background: var(--colors-gray-030);
  }
  &:hover {
    cursor: pointer;
  }
`;
Wrapper.defaultProps = {
  py: 'spacing8',
  px: 'spacing16',
};
